import { useMemo } from 'react';
import { useSearchParams } from 'marketplace-ui-common/hooks/useSearchParams';
import { useLocation, useParams } from 'quartz-routing/react-router';
import { Paths } from '../constants/paths';
import { useGates } from './useGates';
const VALID_PRODUCT_TYPES = new Set(['APP', 'AGENT', 'SOLUTIONS_PARTNER_PROFILE', 'WORKFLOW_TEMPLATE']);
export function useProductTypeSearchParams() {
  const {
    getList
  } = useSearchParams();
  return getList('PRODUCT_TYPE').filter(v => VALID_PRODUCT_TYPES.has(v));
}

/**
 * @deprecated Prefer `useProductTypes()`, which returns an array of
 * product types instead of a singular one (useful for situations that apply to both apps and agents),
 * and is more intelligent than just the param.
 */
export function useProductTypeSearchParam() {
  const productTypes = useProductTypeSearchParams();
  const location = useLocation();
  if (location.pathname === Paths.SEARCH_RESULTS_SOLUTIONS_PARTNERS) {
    return 'SOLUTIONS_PARTNER_PROFILE';
  }
  if (location.pathname === Paths.WORKFLOW_TEMPLATES) {
    return 'WORKFLOW_TEMPLATE';
  }
  return productTypes.length === 1 ? productTypes[0] : undefined;
}

/**
 * The definitive source of truth on which product types are currently applicable,
 * based on the pathname, search params, and gates.
 */
export function useProductTypes() {
  const location = useLocation();
  const params = useSearchParams();
  const isCollection = !!useParams().collectionSlug;
  const {
    workflowTemplatesPhase2
  } = useGates();
  const rawProductTypeParam = params.get('PRODUCT_TYPE', {
    allowedValues: ['APP', 'AGENT', 'SOLUTIONS_PARTNER_PROFILE', 'WORKFLOW_TEMPLATE']
  });
  const productTypeParam = rawProductTypeParam === 'WORKFLOW_TEMPLATE' && !workflowTemplatesPhase2 ? undefined : rawProductTypeParam;
  return useMemo(() => {
    // Currently, we only support apps for collections
    if (isCollection) {
      return ['APP'];
    }

    // "All Products" page
    if (location.pathname === Paths.SEARCH_RESULTS_PRODUCTS) {
      return productTypeParam ? [productTypeParam] : workflowTemplatesPhase2 ? ['APP', 'AGENT', 'WORKFLOW_TEMPLATE'] : ['APP', 'AGENT'];
    }

    // Workflow templates landing page
    if (location.pathname === Paths.WORKFLOW_TEMPLATES) {
      return ['WORKFLOW_TEMPLATE'];
    }

    // Solutions partners homepage OR "All Solutions Partners" page
    if (location.pathname === Paths.SOLUTIONS_PARTNERS || location.pathname === Paths.SEARCH_RESULTS_SOLUTIONS_PARTNERS) {
      return ['SOLUTIONS_PARTNER_PROFILE'];
    }

    // Apps homepage
    if (location.pathname === Paths.APPS) return ['APP'];

    // Agents homepage
    if (location.pathname === Paths.AGENTS) return ['AGENT'];

    // "All" search tab
    if (location.pathname === Paths.SEARCH_RESULTS && !productTypeParam) {
      return workflowTemplatesPhase2 ? ['APP', 'AGENT', 'SOLUTIONS_PARTNER_PROFILE', 'WORKFLOW_TEMPLATE'] : ['APP', 'AGENT', 'SOLUTIONS_PARTNER_PROFILE'];
    }
    return productTypeParam ? [productTypeParam] : [];
  }, [isCollection, location.pathname, productTypeParam, workflowTemplatesPhase2]);
}